import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import styled from "@emotion/styled";
import BulkHeader from "@components/BulkHeader";
import HowWeCanHelpItem from "@components/HowWeCanHelpItem";
import BulkMain from "@components/BulkMain";
import ExchangeLogos from "@components/ExchangeLogos";
import "../styles/exchangeLp.scss";
import { useDispatch } from "react-redux";
import { initAppBrowser } from "@actions/initApp";

const MainContent = styled.div`
  padding-top: 8%;
  padding-bottom: 8%;

  @media (max-width: 630px) {
    padding-top: 100px;
    padding-bottom: 100px;
  }
`;

const AboutContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 82px 0.7fr;

  @media (max-width: 1350px) {
    grid-template-columns: 1fr 135px 0.7fr;
  }
  @media (max-width: 1200px) {
    grid-template-columns: 1fr 140px 0.8fr;
  }
  @media (max-width: 1150px) {
    grid-template-columns: 1fr;
  }
`;

const ExchangeImageContainer = styled.div`
  grid-column: 1/3;
  grid-row: 1/2;

  @media (max-width: 1150px) {
    grid-row: 2/3;
    grid-column: 1/2;
  }
`;

const ExchangeImage = styled.img`
  width: 100%;

  @media (max-width: 1350px) {
    position: relative;
    right: 55px;
  }
  @media (max-width: 1200px) {
    right: 64px;
  }
  @media (max-width: 1150px) {
    right: -67px;
    margin-top: 40px;
    max-width: 868px;
  }
  @media (max-width: 950px) {
    right: -101px;
    max-width: 630px;
  }
  @media (max-width: 850px) {
    max-width: 90%;
  }
  @media (max-width: 730px) {
    max-width: 88%;
    right: -73px;
  }
  @media (max-width: 530px) {
    max-width: 86%;
    right: -50px;
  }
`;

const AboutTextContainer = styled.div`
  grid-column: 2/4;
  grid-row: 1/2;

  @media (max-width: 1150px) {
    grid-row: 1/2;
    grid-column: 1/2;
    text-align: center;
  }
`;

const PurpleLine = styled.div`
  width: 36px;
  height: 3px;
  border-radius: 1.5px;
  background-color: #7557e6;
  margin: ${props => (props.margin0 ? "0 auto" : "")};
`;

const AboutHeader = styled.div`
  font-size: 48px;
  line-height: 1.5;
  color: #1f2021;
  margin-bottom: 41px;
  margin-top: 25px;

  @media (max-width: 1750px) {
    font-size: 45px;
    margin-bottom: 35px;
    margin-top: 22px;
  }
  @media (max-width: 1350px) {
    font-size: 40px;
    margin-bottom: 25px;
    margin-top: 20px;
  }
  @media (max-width: 1200px) {
    font-size: 35px;
    margin-bottom: 20px;
  }
  @media (max-width: 1150px) {
    font-size: 45px;
    margin-bottom: 32px;
  }
  @media (max-width: 500px) {
    font-size: 35px;
  }
`;

const AboutText = styled.div`
  font-size: 18px;
  line-height: 2;
  color: #777984;
  margin-bottom: 23px;

  @media (max-width: 1850px) {
    font-size: 17px;
  }
  @media (max-width: 1750px) {
    font-size: 16px;
    line-height: 1.9;
    margin-bottom: 15px;
  }
  @media (max-width: 1200px) {
    font-size: 15px;
    margin-bottom: 13px;
  }
  @media (max-width: 1150px) {
    font-size: 17px;
    max-width: 575px;
    margin: 0 auto;
    margin-bottom: 23px;
  }
`;

const WhyJoin = styled.div`
  text-align: center;
  padding-top: 145px;
`;

const WhyJoinHeader = styled.div`
  font-size: 44px;
  line-height: 1.42;
  color: #1f2021;
  margin: 0 auto;
  margin-top: 39px;
  margin-bottom: 56px;
  width: 500px;

  @media (max-width: 550px) {
    width: 90%;
    font-size: 36px;
  }
  @media (max-width: 465px) {
    width: 90%;
    font-size: 32px;
  }
  @media (max-width: 415px) {
    width: 90%;
    font-size: 28px;
  }
  @media (max-width: 350px) {
    width: 90%;
    font-size: 25px;
  }
`;

const WhyJoinGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 48px;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr 1fr;
    width: 80%;
    margin: 0 auto;
  }
  @media (max-width: 767px) {
    width: 100%;
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const howItWorksArray = [
  {
    text: "Customers have gift cards from competitor brands but are seeking more diverse options.",
    image: "https://cdn.cardcash.com/website/ui/exchange_lp/step1.svg"
  },
  {
    text: "Via the exchange the gift cards are exchanged to your brand, shifting spending power directly to your business.",
    image: "https://cdn.cardcash.com/website/ui/exchange_lp/step2.svg"
  },
  {
    text: "Customers engage and make purchases from your brand, enhancing customer satisfaction and significantly boosting your sales.",
    image: "https://cdn.cardcash.com/website/ui/exchange_lp/step3.svg"
  },
  {
    text: "Customers get to shop with their preferred brand, and your brand sees increased revenue and acquires new customers.",
    image: "https://cdn.cardcash.com/website/ui/exchange_lp/step4.svg"
  }
];

const HowWeHelpArray = [
  {
    id: 1,
    header: "Access unutilized value",
    text: "Take advantage of CardCash's market position and gain access to millions in untapped gift cards, offering your brand an exceptional opportunity to benefit from this latent value.",
    image: "https://cdn.cardcash.com/website/ui/exchange_lp/customers.svg"
  },
  {
    id: 2,
    header: "Managed and secure",
    text: "CardCash covers all the technical aspects, including system integrations and robust fraud prevention, ensuring a smooth, resource-free expansion of your brand.",
    image: "https://cdn.cardcash.com/website/ui/exchange_lp/support2.svg"
  },
  {
    id: 3,
    header: "Brand equity",
    text: "Our program not only attracts new customers from competitors but also provides your existing customers with a novel way to lock additional value.",
    image: "https://cdn.cardcash.com/website/ui/exchange_lp/cash5.svg"
  }
];

const ExchangeLp = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(initAppBrowser(["messages"]));
  }, []);

  return (
    <div className="mar-t-125 exchange-main">
      <Helmet
        title={
          "Transform your competitors’ customers into your own with a CardCash gift card exchange platform"
        }
        meta={[
          {
            name: "description",
            content:
              "CardCash has been helping businesses save money by taking advantage of unwanted gift cards. The CardCash Exchange platforms allow you to turn your competition’s customers into loyal customers  for your company."
          }
        ]}
      />
      <div>
        <BulkHeader
          buttonText={"Learn more"}
          classFromParent={"exchange-header"}
          headerOne={"Turn unused gift cards into a"}
          headerTwo={"customer acquisition channel"}
          text={
            "Leverage CardCash's position in the secondary gift card market to convert unused gift cards into your own branded currency, creating a new avenue for customer engagement and revenue growth for your business."
          }
          image={"https://cdn.cardcash.com/website/ui/exchange_lp/hero5.png"}
          hideButton={true}
          mobileImage={
            "https://cdn.cardcash.com/website/ui/exchange_lp/mockup-mobile2.png"
          }
        />
        <MainContent className={"main-container-page-width"}>
          <ExchangeLogos />
          <AboutContainer>
            <ExchangeImageContainer>
              <ExchangeImage
                src="https://cdn.cardcash.com/website/ui/exchange_lp/hero6.png"
                alt="exchange"
              />
            </ExchangeImageContainer>
            <AboutTextContainer>
              <PurpleLine className="mobile-center" />
              <AboutHeader>Customer acquisition from competitors</AboutHeader>
              <AboutText>
                The exchange process directly targets customers who are already
                engaged with other brands, presenting them with a seamless
                opportunity to transition their allegiance to your brand.
              </AboutText>
            </AboutTextContainer>
          </AboutContainer>
          <WhyJoin>
            <PurpleLine margin0={true} />
            <WhyJoinHeader>
              Maximize your brand impact with CardCash
            </WhyJoinHeader>
            <WhyJoinGrid>
              {HowWeHelpArray.map((item, index) => (
                <HowWeCanHelpItem
                  itemId={item.id}
                  key={index}
                  header={item.header}
                  text={item.text}
                  image={item.image}
                  bottomContainer={item.bottomContainer ? true : false}
                  backgroundImageColor={"rgba(117, 87, 230, 0.06)"}
                />
              ))}
            </WhyJoinGrid>
          </WhyJoin>
        </MainContent>
        <BulkMain
          classFromParent={"exchange-bulk-main"}
          howItWorksArray={howItWorksArray}
          header={"The CardCash Exchange platform"}
          bottomLeftImage={
            "https://cdn.cardcash.com/website/ui/exchange_lp/elements-left.svg"
          }
          meetPerson={"Joe Rice"}
          personStatement={
            "As the Director of Business Development, Joe's expertise lies in harnessing the potential of gift cards to foster brand growth. His strategic insights have been key in helping brands leverage this niche to their advantage.            "
          }
          personFirstP={
            "Joe is passionate about driving brand growth, specializing in expanding market share and customer base. His extensive work in the gift card space has been pivotal in propelling companies to success."
          }
          personPicture={
            "https://cdn.cardcash.com/website/ui/exchange_lp/joerice-headshot2.png"
          }
        />
      </div>
    </div>
  );
};

export default ExchangeLp;
